/**
 * Preorder cart-mixing guard.
 *
 * Rule: a cart must be entirely preorder items or entirely normal items,
 * never both — a mixed order gets fully held by the 3PL backend until the
 * preorder item is ready, which can hold a normal in-stock item for months.
 *
 * Three layers, only the first two live in this file:
 *   0. Signal — snippets/preorder-form-fields.liquid writes properties[_preorder]
 *      on the cart line itself; layout/theme.liquid always writes
 *      window.WAK_CART_PREORDER as a cheap local snapshot of "does the cart
 *      already have one kind, the other, or both" (server-rendered, on every
 *      page load — a cart that's all-normal still needs to say so, or a
 *      preorder item added afterwards has nothing to check itself against).
 *   1. Block on add — this file, a capturing `submit` listener that runs before
 *      product-form.js's own handler. Layer 1c (also here) covers adds that
 *      apps post straight to /cart/add, but only while the cart holds a
 *      product tagged `preorder` (the advent calendar).
 *   2. Checkout backstop — assets/global.js calls window.PreorderCartGuard.isMixed()
 *      inside CheckoutButton.buildCartProperties(), which already fetches the
 *      cart on every checkout click today. That is the real safety net; this
 *      file's job is to fail early and kindly, not to be airtight.
 *
 * Near-zero-cost, not strictly zero, for a shopper who never touches a
 * preorder item: the local check (incoming item preorder XOR cart already
 * has the other kind) is always evaluated first, from data already in
 * memory — no fetch happens on add unless that check says a block is
 * actually possible. The one deliberate exception is the re-check safety net
 * below (visibilitychange/pageshow first, click as a fallback for what those
 * can't catch), which runs for every shopper but is throttled and, for the
 * primary triggers, tied to genuinely rare events (regaining tab focus,
 * bfcache restore) rather than every click — window.WAK_CART_PREORDER is
 * only ever computed once, at this page's own load, so a preorder item added
 * in a different tab or an earlier page load leaves nothing to tell this
 * page it's now stale — confirmed live, where that let a real conflict
 * through with no warning at all. A stale local flag can cause a missed
 * block (allowed through, Layer 2
 * catches it at checkout) but essentially never a wrong one on its own — the
 * one other exception is if the confirming /cart.js read itself fails after
 * retrying (observed in practice: Cloudflare's bot protection intermittently
 * challenging that exact call). In that specific case the local suspicion is
 * trusted and the add is blocked rather than silently allowed, since by then
 * it is already reliable enough that assuming "no conflict" from an
 * unrelated network failure is the riskier default.
 */
(function () {
  if (window.PreorderCartGuard) return;

  var snapshot = window.WAK_CART_PREORDER || {};

  // Variant ids of products tagged `preorder` (the advent calendar) — from the
  // cart lines layout/theme.liquid saw, plus any tagged product form rendered
  // on this page (preorder-form-fields.liquid). A line of one of these is a
  // preorder even without its _preorder property.
  var taggedVariantIds = {};
  function rememberTagged(ids) {
    (ids || []).forEach(function (id) {
      if (id) taggedVariantIds[String(id)] = true;
    });
  }
  function isTaggedVariant(id) {
    return id != null && taggedVariantIds[String(id)] === true;
  }
  rememberTagged(snapshot.taggedVariantIds);

  var state = {
    hasPreorder: Boolean(snapshot.hasPreorder),
    hasNormal: Boolean(snapshot.hasNormal),
    hasTagged: Boolean(snapshot.taggedVariantIds && snapshot.taggedVariantIds.length),
  };

  // Bounded, short-lived overlay for adds that succeeded locally but haven't
  // shown up in a /cart.js read yet — closes the "double-click" and "two
  // rapid adds" races without ever needing to trust it for longer than it
  // takes the cart to actually update.
  var pendingAdds = [];
  var PENDING_TTL_MS = 15000;

  function prunePending() {
    var now = Date.now();
    pendingAdds = pendingAdds.filter(function (p) {
      return now - p.at < PENDING_TTL_MS;
    });
  }

  // A cart line worth $0 is treated as neutral — the UpCart loyalty app
  // automatically adds a free reward line (properties like
  // __upcartRewardProduct/__upcartTier) alongside real purchases, and that is
  // not the "other item" this guard exists to catch. Classifying by price
  // rather than by property name survives UpCart renaming things, or another
  // free-gift app being installed later (this store also has a "givy" app
  // embed active).
  function isSystemLine(line) {
    return Number(line.price) === 0;
  }

  function isTaggedLine(line) {
    return isTaggedVariant(line.variant_id || line.id);
  }

  function isPreorderLine(line) {
    return (line.properties && line.properties._preorder === 'true') || isTaggedLine(line);
  }

  // Recomputes state from a real cart payload (either a full /cart.js body,
  // or the single-line response /cart/add.js returns). Never trusted as the
  // sole source for a block — only ever used to decide whether to look
  // further, or (via refreshFromCart) to keep the cheap local check honest.
  function classify(lines) {
    var relevant = lines.filter(function (l) { return !isSystemLine(l); });
    var hasPreorder = relevant.some(isPreorderLine);
    var hasNormal = relevant.some(function (l) { return !isPreorderLine(l); });
    var hasTagged = relevant.some(isTaggedLine);
    return { hasPreorder: hasPreorder, hasNormal: hasNormal, hasTagged: hasTagged };
  }

  function refreshFromCart(cart) {
    var lines = (cart && cart.items) || [];
    var next = classify(lines);
    state.hasPreorder = next.hasPreorder;
    state.hasNormal = next.hasNormal;
    state.hasTagged = next.hasTagged;
    pendingAdds = [];
    if (next.hasPreorder) ensurePolling();
    if (next.hasTagged) installAppAddGuard();
  }

  function fetchCartOnce() {
    return fetch('/cart.js', { credentials: 'include', cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
      return r.json();
    });
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // Bounded retries, not a loop: observed in testing that this exact endpoint
  // can intermittently come back as a non-JSON challenge page (Cloudflare bot
  // protection) instead of the real cart, when it fires close together with
  // other requests — the confirm-before-block check silently failed open on
  // that response, letting a real conflict through with no warning. Also
  // observed: a single retry isn't always enough during a burst of traffic
  // (heavy testing in a short window made the same page get challenged twice
  // in a row) — two retries with a growing delay gives more real chances to
  // land on a moment where the challenge has cleared. This is a confirmation
  // check, not something worth chasing forever: if all three attempts fail,
  // evaluateAdd's own catch still fails open (by design — Layer 2 at
  // checkout never fails open on a confirmed mix, regardless of this).
  function fetchCart() {
    return fetchCartOnce()
      .catch(function () { return delay(400).then(fetchCartOnce); })
      .catch(function () { return delay(900).then(fetchCartOnce); });
  }

  // Serializes decisions so two near-simultaneous submits are judged one
  // after the other against a state that already reflects the first one,
  // instead of both reading the same stale cart.
  var chain = Promise.resolve();

  /**
   * @param {{variantId: string, isPreorder: boolean}} req
   * @returns {Promise<{allowed: boolean, reasonKey?: string}>}
   */
  function evaluateAdd(req) {
    chain = chain.catch(function () {}).then(function () {
      // Local state only updates from confirmed cart reads (refreshFromCart);
      // an add allowed moments ago via the fast path below hasn't landed
      // there yet, only in pendingAdds. Folding pendingAdds into the suspect
      // check too is what actually closes the rapid-opposite-type-adds race —
      // without it, two near-simultaneous adds from a neutral starting state
      // could both take the fast path and never cross-check each other.
      prunePending();
      var pendingHasPreorder = pendingAdds.some(function (p) { return p.isPreorder; });
      var pendingHasNormal = pendingAdds.some(function (p) { return !p.isPreorder; });
      var effectiveHasPreorder = state.hasPreorder || pendingHasPreorder;
      var effectiveHasNormal = state.hasNormal || pendingHasNormal;
      var suspect = req.isPreorder ? effectiveHasNormal : effectiveHasPreorder;
      if (!suspect) {
        pendingAdds.push({ variantId: req.variantId, isPreorder: req.isPreorder, at: Date.now() });
        if (isTaggedVariant(req.variantId)) installAppAddGuard();
        // A preorder item just entered the cart in this page session — start
        // watching for UpCart's own (pre-add-hookless) upsell/removal events
        // now, not just when window.WAK_CART_PREORDER happened to be true at
        // this page's initial render. Without this, a shopper who adds their
        // first preorder item via an ordinary product form, then a normal
        // item via an UpCart drawer upsell rail (no page reload in between),
        // never gets the post-hoc auto-remove safety net, because the
        // server-rendered flag this used to gate on is frozen at page-load.
        if (req.isPreorder) ensurePolling();
        return { allowed: true };
      }

      return fetchCart().then(function (cart) {
        prunePending();
        var lines = cart.items.concat(
          pendingAdds.map(function (p) {
            return { id: p.variantId, price: 1, properties: p.isPreorder ? { _preorder: 'true' } : {} };
          })
        );
        var real = classify(lines);
        state.hasPreorder = real.hasPreorder;
        state.hasNormal = real.hasNormal;
        state.hasTagged = real.hasTagged;

        var conflict = req.isPreorder ? real.hasNormal : real.hasPreorder;
        if (!conflict) {
          pendingAdds.push({ variantId: req.variantId, isPreorder: req.isPreorder, at: Date.now() });
          if (isTaggedVariant(req.variantId)) installAppAddGuard();
          if (req.isPreorder) ensurePolling();
          return { allowed: true };
        }

        return { allowed: false, reasonKey: req.isPreorder ? 'addingPreorder' : 'addingRegular' };
      }).catch(function () {
        // Reaching this catch means BOTH: the local state (server-rendered
        // at page load, kept current via real cart events since) already
        // said a conflict was likely enough to be worth confirming, AND the
        // confirmation itself failed after retries — observed in practice
        // to often be Cloudflare's bot protection intermittently challenging
        // this exact call rather than a genuine "nothing to worry about".
        // Given how reliable that local signal already is, trusting it here
        // (block) is the safer default than assuming the fetch failure means
        // no conflict (allow): a wrongly-blocked add just needs one more
        // click to retry, while a wrongly-allowed one is the exact silent
        // failure this whole feature exists to prevent. Layer 2 at checkout
        // still confirms independently regardless of this decision.
        return { allowed: false, reasonKey: req.isPreorder ? 'addingPreorder' : 'addingRegular' };
      });
    });
    return chain;
  }

  function isMixed(items) {
    var result = classify(items || []);
    return result.hasPreorder && result.hasNormal;
  }

  function conflictingLines(items, wantPreorder) {
    return (items || []).filter(function (l) {
      return !isSystemLine(l) && isPreorderLine(l) === wantPreorder;
    });
  }

  function resolveMessage(reasonKey) {
    var strings = window.preorderStrings || {};
    return strings[reasonKey] || '';
  }

  // ---- Layer 1: block on add ---------------------------------------------

  // Always read the flag input itself, never .goshop-mp-hu's own
  // hasPreorderColor dataset directly. Both are supposed to agree — buy-
  // buttons.liquid's applyPreorderState sets the flag's disabled state from
  // the exact same hasPreorder value every time a colour slot changes — but
  // they are two separately-written pieces of state, and reading
  // hasPreorderColor here duplicated a sync that already happens elsewhere.
  // A real cart-mixing case observed in testing traced to exactly this: the
  // container's dataset briefly disagreed with the flag at the moment of
  // submit, and this function had trusted the wrong one — approving an add
  // as "preorder" when the form data about to be submitted said otherwise.
  // Reading the flag directly makes this the single source of truth for
  // what's actually in the FormData, so the guard's decision can never
  // diverge from the real cart line: worst case is a missed block (Layer 2
  // still catches it), never a wrong one.
  function isPreorderForm(productFormEl) {
    var flag = productFormEl ? productFormEl.querySelector('[data-preorder-flag]') : null;
    return Boolean(flag && !flag.disabled);
  }

  function setPending(productFormEl, pending) {
    var button = productFormEl.querySelector('[type="submit"]');
    if (!button) return;
    button.classList.toggle('loading', pending);
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !pending);
  }

  function showBlocked(productFormEl, message) {
    if (!message) return;
    // Both, not either/or: the inline message is easy to miss if the form is
    // scrolled out of view or sits behind a cart drawer overlay (the drawer
    // covers the whole page on mobile), and the toast alone lacks the
    // context of being anchored right at the button the shopper just
    // clicked. Showing both costs nothing extra — same evaluateAdd() result,
    // just rendered twice.
    showToast(message);
    if (typeof productFormEl.handleErrorMessage === 'function') {
      productFormEl.handleErrorMessage(message);
      var wrapper = productFormEl.querySelector('.product-form__error-message-wrapper');
      if (wrapper) {
        var rect = wrapper.getBoundingClientRect();
        var offscreen = rect.top < 0 || rect.bottom > window.innerHeight;
        if (offscreen) {
          wrapper.setAttribute('tabindex', '-1');
          wrapper.scrollIntoView({ block: 'center' });
          wrapper.focus();
        }
      }
    }
  }

  var toastMessageEl = null;
  function buildToast() {
    var toast = document.createElement('div');
    toast.id = 'preorder-guard-toast';
    toast.setAttribute('role', 'alert');
    // left/right + margin:auto instead of left:50%+transform: fills the
    // width on narrow screens (minus a 16px margin each side) instead of
    // topping out at a fixed max-width that leaves the toast looking small
    // and off-center on mobile; on wider screens the same rule just centers
    // it at up to 420px.
    toast.style.cssText =
      'position:fixed;left:16px;right:16px;bottom:24px;margin:0 auto;' +
      'display:flex;align-items:flex-start;gap:10px;' +
      'background:#1f2937;color:#f9fafb;padding:14px 14px 14px 16px;border-radius:14px;' +
      'font-size:14px;line-height:1.45;max-width:420px;' +
      'box-shadow:0 12px 32px rgba(0,0,0,.28);z-index:2147483647;';

    var icon = document.createElement('span');
    icon.textContent = '⚠'; // ⚠
    icon.setAttribute('aria-hidden', 'true');
    icon.style.cssText = 'flex:none;font-size:16px;line-height:1.4;';

    var text = document.createElement('span');
    text.id = 'preorder-guard-toast-message';
    text.style.cssText = 'flex:1;';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×'; // ×
    closeBtn.style.cssText =
      'flex:none;background:none;border:0;color:#f9fafb;opacity:.6;' +
      'font-size:20px;line-height:1;cursor:pointer;padding:2px;margin:-2px -2px 0 0;';
    closeBtn.addEventListener('mouseenter', function () { closeBtn.style.opacity = '1'; });
    closeBtn.addEventListener('mouseleave', function () { closeBtn.style.opacity = '.6'; });
    closeBtn.addEventListener('click', function () { toast.style.display = 'none'; });

    toast.appendChild(icon);
    toast.appendChild(text);
    toast.appendChild(closeBtn);
    toastMessageEl = text;
    return toast;
  }

  function showToast(message) {
    var toast = document.getElementById('preorder-guard-toast') || buildToast();
    // Re-append (not just create-once) on every show, to <html> rather than
    // <body>: a cart-drawer app commonly locks background scroll by putting
    // a transform/overflow-hidden on <body> while it's open, which creates a
    // new containing block that traps position:fixed descendants inside it —
    // no z-index value escapes that from the inside. Living as the last
    // child of <html> (a sibling of <body>, not inside it) and being the
    // most-recently-appended element sidesteps both that containment and any
    // equal-z-index paint-order tiebreak against the drawer's own overlay.
    document.documentElement.appendChild(toast);
    toastMessageEl.textContent = message;
    toast.style.display = 'flex';
  }

  function onAddToCartSubmit(evt) {
    var form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;
    if (form.__preorderGuardCleared) {
      form.__preorderGuardCleared = false;
      return;
    }

    // Checked before preventDefault/stopImmediatePropagation, not after: if
    // either lookup fails, the right behavior is to not touch this submit at
    // all and let product-form.js's own handler run normally — hijacking the
    // event and then bailing here would silently swallow the add-to-cart
    // click with no recovery (no error, no re-dispatch, nothing happens).
    var productFormEl = form.closest('product-form');
    var idInput = form.querySelector('[name="id"]');
    if (!productFormEl || !idInput) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    var taggedFlag = productFormEl.querySelector('[data-preorder-tagged]');
    if (taggedFlag) rememberTagged(taggedFlag.dataset.preorderTagged.split(','));

    var req = { variantId: idInput.value, isPreorder: isPreorderForm(productFormEl) };

    setPending(productFormEl, true);
    evaluateAdd(req).then(function (result) {
      setPending(productFormEl, false);
      if (!result.allowed) {
        showBlocked(productFormEl, resolveMessage(result.reasonKey));
        return;
      }
      form.__preorderGuardCleared = true;
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  document.addEventListener('submit', onAddToCartSubmit, true);

  // Keep state fresh for free, from responses the theme already produces —
  // never a new fetch just to stay in sync.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      fetchCart().then(refreshFromCart).catch(function () {});
    });
    if (PUB_SUB_EVENTS.cartError) {
      subscribe(PUB_SUB_EVENTS.cartError, function () {
        pendingAdds = [];
      });
    }
  }

  // ---- Layer 1b: the (currently dormant) drawer recommendation rail ------
  // snippets/cart-drawer.liquid's own click handler calls this before its
  // raw fetch('/cart/add.js', ...) — see that file for the wiring. Dormant
  // today (cart_type setting is "notification"), guarded anyway since it is
  // one theme-editor toggle away from being live.
  function guardRecoAdd(button) {
    if (button.dataset.preorderTagged) rememberTagged(button.dataset.preorderTagged.split(','));
    return evaluateAdd({
      variantId: button.dataset.variantId,
      isPreorder: button.dataset.preorder === 'true',
    });
  }

  // ---- Layer 1c: adds made by apps, only while the advent calendar is in the cart
  // UpCart's drawer upsells and Essential Upsell's PDP block post straight to
  // /cart/add.js — no form submit, so Layer 1 never sees them, and real
  // orders show both getting a normal item into a cart that already held the
  // advent calendar. This wraps fetch (and XHR) to look at those requests.
  //
  // Installed only once the cart holds a product tagged `preorder` (page load
  // snapshot, a confirmed cart read, or an allowed add of that product). A
  // shopper who never has it in the cart never gets fetch or XHR touched.
  // Once installed it stays, but passes everything through untouched the
  // moment that product leaves the cart (isTaggedActive() goes false).
  //
  // Fails open everywhere: a request it can't read is let through.
  var appAddGuardInstalled = false;

  function isTaggedActive() {
    prunePending();
    return state.hasTagged || pendingAdds.some(function (p) { return isTaggedVariant(p.variantId); });
  }

  function isCartAddRequest(method, url) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    try {
      return /\/cart\/add(\.js|\.json)?$/.test(new URL(String(url), window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  // Accepts every body shape /cart/add takes: JSON ({id} or {items: [...]}),
  // FormData, URLSearchParams, or a urlencoded string — including
  // items[0][id] style keys. Returns null when no item id can be read.
  function itemsFromBody(body) {
    if (body == null) return null;
    if (typeof body === 'string') {
      try {
        return itemsFromJson(JSON.parse(body));
      } catch (e) {
        return itemsFromEntries(new URLSearchParams(body));
      }
    }
    if (
      (typeof FormData !== 'undefined' && body instanceof FormData) ||
      (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
    ) {
      return itemsFromEntries(body);
    }
    return null;
  }

  function itemsFromJson(data) {
    if (!data || typeof data !== 'object') return null;
    var list = Array.isArray(data.items) ? data.items : data.id != null ? [data] : null;
    if (!list || !list.length) return null;
    var items = list.map(function (i) {
      return { id: i && i.id != null ? String(i.id) : null, quantity: Number((i && i.quantity) || 1), properties: (i && i.properties) || {} };
    });
    return items.every(function (i) { return i.id; }) ? items : null;
  }

  function itemsFromEntries(entries) {
    var byIndex = {};
    entries.forEach(function (value, key) {
      var m = /^items\[(\d+)\]\[(\w+)\](?:\[(.+)\])?$/.exec(key);
      var index = m ? m[1] : 'single';
      var field = m ? m[2] : key;
      var prop = m ? m[3] : null;
      if (!m) {
        var pm = /^properties\[(.+)\]$/.exec(key);
        if (pm) {
          field = 'properties';
          prop = pm[1];
        }
      }
      var item = byIndex[index] || (byIndex[index] = { id: null, quantity: 1, properties: {} });
      if (field === 'id') item.id = String(value);
      else if (field === 'quantity') item.quantity = Number(value) || 1;
      else if (field === 'properties' && prop) item.properties[prop] = String(value);
    });
    var items = Object.keys(byIndex).map(function (k) { return byIndex[k]; });
    if (!items.length || !items.every(function (i) { return i.id; })) return null;
    return items;
  }

  // UpCart's own free reward line — neutral, same as isSystemLine() treats it
  // once it is in the cart (its price is 0 there, but a request has no price).
  function isRewardItem(item) {
    return Object.keys(item.properties).some(function (k) {
      return k === '__upcartRewardProduct' || k === '__upcartTriggeredReward';
    });
  }

  function isPreorderItem(item) {
    return item.properties._preorder === 'true' || isTaggedVariant(item.id);
  }

  function appAddConflicts(items) {
    if (!items || !isTaggedActive()) return false;
    return items.some(function (i) { return !isRewardItem(i) && !isPreorderItem(i); });
  }

  function blockedAddResponse(message) {
    // The shape Shopify itself returns for a refused add — UpCart reads
    // `message` from a non-ok response, so its own UI gets the same text.
    return new Response(JSON.stringify({ status: 422, message: message, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function refreshUpcartDrawer() {
    if (typeof window.upcartRefreshCart === 'function') {
      try {
        window.upcartRefreshCart();
      } catch (e) {}
    }
  }

  // XHR can't be answered with a fake response cleanly, so for XHR the add is
  // let through and the lines it just added are taken straight back out —
  // only the quantity that request added, by the line keys its own response
  // names.
  function confirmFromCart() {
    return fetchCart().then(refreshFromCart, function () {});
  }

  function readXhrJson(xhr) {
    try {
      if (xhr.responseType === 'json') return xhr.response;
      if (xhr.responseType === '' || xhr.responseType === 'text') return JSON.parse(xhr.responseText);
    } catch (e) {}
    return null;
  }

  function undoXhrAdd(xhr, items) {
    var added = readXhrJson(xhr);
    if (!added) return;
    var lines = added && Array.isArray(added.items) ? added.items : added && added.key ? [added] : [];
    var requestedById = {};
    items.forEach(function (i) {
      if (!isRewardItem(i) && !isPreorderItem(i)) requestedById[i.id] = (requestedById[i.id] || 0) + i.quantity;
    });
    var updates = {};
    lines.forEach(function (l) {
      var requested = requestedById[String(l.variant_id || l.id)];
      if (requested && l.key) updates[l.key] = Math.max(0, Number(l.quantity) - requested);
    });
    if (!Object.keys(updates).length) return;
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        refreshFromCart(cart);
        refreshUpcartDrawer();
      })
      .catch(function () {});
    showToast(resolveMessage('addingRegular'));
  }

  function installAppAddGuard() {
    if (appAddGuardInstalled) return;
    appAddGuardInstalled = true;

    var previousFetch = window.fetch;
    if (typeof previousFetch === 'function') {
      window.fetch = function (input, init) {
        var args = arguments;
        var passThrough = function () { return previousFetch.apply(window, args); };
        if (!isTaggedActive()) return passThrough();

        var isRequest = typeof Request !== 'undefined' && input instanceof Request;
        var url = isRequest ? input.url : input;
        var method = (init && init.method) || (isRequest ? input.method : 'GET');
        if (!isCartAddRequest(method, url)) return passThrough();

        var bodyRead =
          init && init.body != null
            ? Promise.resolve(init.body)
            : isRequest
            ? input.clone().text()
            : Promise.resolve(null);
        return bodyRead.then(
          function (body) {
            var items = null;
            try {
              items = itemsFromBody(body);
            } catch (e) {}
            if (!appAddConflicts(items)) return passThrough();
            // Local state can lag behind a removal this page never heard
            // about — confirm against the real cart before refusing. If that
            // read fails, the local state stands (same as evaluateAdd).
            return confirmFromCart().then(function () {
              if (!appAddConflicts(items)) return passThrough();
              var message = resolveMessage('addingRegular');
              showToast(message);
              return blockedAddResponse(message);
            });
          },
          passThrough
        );
      };
    }

    if (typeof XMLHttpRequest !== 'undefined') {
      var originalOpen = XMLHttpRequest.prototype.open;
      var originalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          this.__preorderGuardIsAdd = isCartAddRequest(method, url);
        } catch (e) {}
        return originalOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function (body) {
        try {
          if (this.__preorderGuardIsAdd && isTaggedActive()) {
            var items = itemsFromBody(body);
            if (appAddConflicts(items)) {
              this.addEventListener('load', function () {
                if (this.status < 200 || this.status >= 300) return;
                var xhr = this;
                confirmFromCart().then(function () {
                  if (appAddConflicts(items)) undoXhrAdd(xhr, items);
                });
              });
            }
          }
        } catch (e) {}
        return originalSend.apply(this, arguments);
      };
    }

    // Nothing to block if UpCart never offers it: hide the drawer's upsell
    // rail while the advent calendar is in the cart. Any earlier override is
    // kept, and outside that case its result is returned unchanged.
    var previousModifyUpsells = window.upcartModifyListOfUpsells;
    window.upcartModifyListOfUpsells = function () {
      var result = typeof previousModifyUpsells === 'function' ? previousModifyUpsells.apply(this, arguments) : undefined;
      return isTaggedActive() ? [] : result;
    };
    refreshUpcartDrawer();
  }

  // ---- UpCart: after-the-fact events only, no pre-add hook exists --------
  // Documented publicly: UpCart exposes no hook that can cancel an add before
  // it happens. These three keep the local snapshot honest after something
  // we had no chance to intercept; window.PreorderCartGuard.isMixed(), used
  // by the checkout backstop in global.js, is what actually guarantees no
  // mixed order reaches payment.
  function pollForUpcart(attemptsLeft) {
    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      // Deliberately ignores the payload this callback receives and re-fetches
      // /cart.js instead — a live bug traced state.hasPreorder getting reset
      // to false, over and over, to exactly this handler trusting whatever
      // shape UpCart hands it here. Nothing documents that payload's exact
      // shape, and this event is one this app can plausibly fire often (every
      // drawer refresh) — feeding it straight into refreshFromCart meant one
      // mismatched or incomplete payload could silently wipe an otherwise-
      // correct state at any moment, not just once at load. /cart.js is the
      // one shape this file already knows for certain, since it also calls
      // it directly elsewhere — matching what upcartSubscribeItemRemoved
      // already does below, for the same reason.
      window.upcartSubscribeCartLoaded(function () {
        fetchCart().then(refreshFromCart).catch(function () {});
      });
    }
    if (typeof window.upcartSubscribeItemRemoved === 'function') {
      window.upcartSubscribeItemRemoved(function () {
        fetchCart().then(refreshFromCart).catch(function () {});
      });
    }
    if (typeof window.upcartSubscribeUpsellsAddedToCart === 'function') {
      window.upcartSubscribeUpsellsAddedToCart(function (payload) {
        fetchCart().then(function (cart) {
          var wasMixed = isMixed(cart.items);
          refreshFromCart(cart);
          if (!wasMixed) return;
          // The one case where auto-removing without asking is justified:
          // this was a one-click app affordance we had no chance to
          // intercept, and we know exactly which line just arrived.
          var addedId = payload && (payload.variantId || payload.id);
          var offending = cart.items.filter(function (l) {
            return !isSystemLine(l) && String(l.variant_id || l.id) === String(addedId);
          })[0];
          if (!offending) return;
          fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: offending.key, quantity: 0 }),
          }).then(function (r) { return r.json(); }).then(function (updated) {
            refreshFromCart(updated);
            showToast(window.preorderStrings && window.preorderStrings.mixedCartNotice);
          });
        });
      });
    }
    var found =
      typeof window.upcartSubscribeCartLoaded === 'function' ||
      typeof window.upcartSubscribeItemRemoved === 'function' ||
      typeof window.upcartSubscribeUpsellsAddedToCart === 'function';
    if (!found && attemptsLeft > 0) {
      setTimeout(function () { pollForUpcart(attemptsLeft - 1); }, 250);
    }
  }

  // ---- Generic safety net: re-check when staleness is actually likely ----
  // pollForUpcart only catches what it has a specific hook for — three
  // named functions, documented publicly, which may not cover every way
  // UpCart can add a line (different app features can use different
  // internal events; this store's version might not fire all three).
  //
  // window.WAK_CART_PREORDER is only ever computed once, at this page's own
  // load — a preorder item added in a different tab, or restored via the
  // browser back button (bfcache, which resurrects this exact JS state
  // without a fresh render), leaves nothing to tell this page it's stale.
  // Confirmed live: evaluateAdd() on such a page returned allowed:true for a
  // real conflict, because state.hasPreorder was still false from that
  // page's own load.
  //
  // Two distinct triggers close two distinct gaps, both throttled through
  // the same checkForMix():
  //  - visibilitychange/pageshow: fires when this tab regains focus or is
  //    restored from bfcache — exactly when the "changed somewhere else"
  //    risk is real, and rare enough in a normal single-tab session that it
  //    costs close to nothing. Precedes the shopper's next click, so it
  //    closes the "can't catch the very first click" gap the click-based
  //    check below can't.
  //  - click: still needed for a same-tab, same-page case neither of the
  //    above covers — an item added through a UI this file has no hook for
  //    (e.g. UpCart's own upsell carousel, confirmed in testing), where
  //    nothing about visibility or bfcache changes at all. Scoped to clicks
  //    inside UpCart's own markup (see scheduleClickTriggeredCheck) and
  //    throttled, so a shopper who never opens the UpCart drawer pays
  //    nothing extra, and one who does pays a small, bounded cost (at most
  //    one /cart.js read per ~25s of clicking inside it) rather than
  //    nothing — accepted because the alternative left a proven gap open.
  //
  // A generic re-check gives no way to know which line is the "intruder" —
  // so this only surfaces the notice and lets the shopper resolve it via the
  // cart page's own remove buttons, rather than guessing which line to remove.
  var recheckTimer = null;
  var mixedNoticeShown = false;
  var lastRecheckAt = 0;
  var RECHECK_MIN_INTERVAL_MS = 25000;
  function checkForMix() {
    lastRecheckAt = Date.now();
    fetchCart().then(function (cart) {
      var mixed = isMixed(cart.items);
      refreshFromCart(cart);
      if (mixed && !mixedNoticeShown) {
        mixedNoticeShown = true;
        showToast(window.preorderStrings && window.preorderStrings.mixedCartNotice);
      } else if (!mixed) {
        mixedNoticeShown = false;
      }
    }).catch(function () {});
  }
  // Scoped to inside UpCart's own markup, not the whole document: this file
  // has no access to UpCart's source, so it can't target its specific
  // "add" button by class or text without risking that guess silently going
  // stale on an app update — but assets/global.js already depends on
  // `.upcart-checkout-button` for the checkout interception, confirming
  // UpCart's own elements consistently carry an "upcart"-prefixed class.
  // Matching any element with "upcart" in its class rides that same
  // precedent for the *container* (the whole drawer) rather than one
  // specific button, which is far less likely to change across an app
  // update than a specific button's class or label text — while still
  // cutting the trigger down from every click on the page to only clicks
  // that happen while the drawer is open.
  function scheduleClickTriggeredCheck(evt) {
    if (Date.now() - lastRecheckAt < RECHECK_MIN_INTERVAL_MS) return;
    var target = evt.target;
    if (!(target instanceof Element) || !target.closest('[class*="upcart"]')) return;
    clearTimeout(recheckTimer);
    recheckTimer = setTimeout(checkForMix, 800);
  }
  document.addEventListener('click', scheduleClickTriggeredCheck, true);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastRecheckAt < RECHECK_MIN_INTERVAL_MS) return;
    checkForMix();
  });
  window.addEventListener('pageshow', function (evt) {
    if (!evt.persisted) return; // only the bfcache-restore case; a normal load already has a fresh window.WAK_CART_PREORDER
    checkForMix();
  });

  // Only worth polling UpCart's own events for at all if there is a chance
  // the cart is (or could become) mixed — a shopper who has never touched
  // preorder gets no extra 250ms-interval timers. Idempotent and callable
  // from multiple places: the server-rendered flag below only reflects cart
  // state as of this page's initial render, so a preorder item added
  // afterwards (no page reload, e.g. through an AJAX drawer) needs
  // evaluateAdd/refreshFromCart to be able to start this too, the moment
  // they see a preorder item for real.
  var pollingStarted = false;
  function ensurePolling() {
    if (pollingStarted) return;
    pollingStarted = true;
    pollForUpcart(40); // ~10s
  }
  if (window.WAK_CART_PREORDER && window.WAK_CART_PREORDER.hasPreorder) {
    ensurePolling();
  }

  // Deferred script: the page's own tagged product forms are already parsed.
  document.querySelectorAll('[data-preorder-tagged]').forEach(function (el) {
    rememberTagged(el.dataset.preorderTagged.split(','));
  });
  if (state.hasTagged) installAppAddGuard();

  window.PreorderCartGuard = {
    evaluateAdd: evaluateAdd,
    isMixed: isMixed,
    conflictingLines: conflictingLines,
    resolveMessage: resolveMessage,
    showToast: showToast,
    guardRecoAdd: guardRecoAdd,
    refreshFromCart: refreshFromCart,
  };
})();
